/**
 * TWL pipeline (provider Renata Silva, 2026-10-23: "how many TWLs are currently in the hearing aid negotiation
 * process ... I still have TWLs dating back to 2024 and I am not sure every TWL has been contacted"; Isaiah Cotto:
 * "I will make them visible for you"). docs/provider/TWL_PIPELINE.md.
 *
 * One read (GET /emerge/day-sheet/twl, twlPipelineApi.ts) for every building the provider may open. Every count is the
 * whole pipeline; residents are named only where the provider covers the building (or is assigned the resident), and
 * elsewhere are counted and said to be not named. Every filter is applied by the server: changing one reads again,
 * the list is paged ("Show more"), and Export CSV is every matching named row.
 *
 * Derived signals only (until Isaiah rules otherwise): no Patient Advocate's name and no note date. "PA activity" is a
 * bucket the server computes. Status is never colour alone: every stage chip carries its word and an icon shape.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BadgeCheck,
  CheckCircle2,
  CircleAlert,
  CircleSlash,
  ClipboardList,
  Clock3,
  Download,
  FileQuestion,
  FileText,
  FileUp,
  Hand,
  Handshake,
  HelpCircle,
  MessageSquareText,
  PackageCheck,
  PauseCircle,
  Send,
  Stethoscope,
  UserRound,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "../../../components/shadcn/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/shadcn/ui/table";
import { StateBlock as TideState } from "../../../components/tide";
import { closureWords } from "../providerApi";
import { FieldSelect, Notice, Section } from "./FieldPrimitives";
import { NO_FILTERS, TWL_AGES, twlPipelineApi } from "./twlPipelineApi";
import type { TwlActivity, TwlAgeFilter, TwlFilters, TwlPipeline, TwlRow, TwlStage } from "./twlPipelineApi";
import RemindPa from "./RemindPa";
import type { TwlRemind } from "./paReminderApi";
import "./field-workspace.css";
import "./field-twl.css";

export const FOOTER_LINE =
  "PA activity says when a Patient Advocate last wrote a note on the case, not that the resident or POA was reached. Older TWLs, and hearing tests over 1 year old, may need a new test before outreach.";
/** The most rows one CSV carries (the server refuses past it). */
export const EXPORT_MAX = 20000;

/** The funnel, earliest work first, in five plain groups. */
const GROUPS: Array<{ key: string; label: string; stages: TwlStage[] }> = [
  { key: "needs", label: "Needs follow-up", stages: ["NO_OPEN_CASE", "WAITING_FOR_PA", "OPEN_OFF_QUEUE", "STATUS_UNRECOGNISED"] },
  { key: "with-pa", label: "With the Patient Advocates", stages: ["WITH_PA_NO_NOTE", "PA_NOTED", "PA_IN_PROGRESS", "PREPARING_FOR_DCF", "ON_HOLD"] },
  { key: "approval", label: "Enrolment and approval", stages: ["ENROLLED", "UMED_IN_PROGRESS", "UMED_UNRECOGNISED", "UMED_SUBMITTED", "DCF_APPROVED"] },
  { key: "aids", label: "Hearing aids", stages: ["ATD_APPROVED", "DISPENSED"] },
  { key: "closed", label: "Outcome recorded", stages: ["CLOSED_OUT"] },
];
const GROUP_OF = Object.fromEntries(GROUPS.flatMap((g) => g.stages.map((s) => [s, g.key]))) as Record<TwlStage, string>;
const ICON: Record<TwlStage, LucideIcon> = {
  NO_OPEN_CASE: CircleAlert,
  WAITING_FOR_PA: Clock3,
  OPEN_OFF_QUEUE: Hand,
  STATUS_UNRECOGNISED: HelpCircle,
  WITH_PA_NO_NOTE: UserRound,
  PA_NOTED: MessageSquareText,
  PA_IN_PROGRESS: ClipboardList,
  PREPARING_FOR_DCF: FileUp,
  ON_HOLD: PauseCircle,
  ENROLLED: Handshake,
  UMED_IN_PROGRESS: FileText,
  UMED_UNRECOGNISED: FileQuestion,
  UMED_SUBMITTED: Send,
  DCF_APPROVED: BadgeCheck,
  ATD_APPROVED: CheckCircle2,
  DISPENSED: PackageCheck,
  CLOSED_OUT: CircleSlash,
};
const AGE_LABEL: Record<TwlAgeFilter, string> = {
  LT_30: "Under 30 days", D30_90: "30 to 90 days", D90_365: "90 days to 1 year", GT_365: "Over 1 year", UNKNOWN: "Date not recorded",
  BEFORE_2025: "2024 or earlier",
};

function StageChip({ stage, label }: { stage: TwlStage; label: string }) {
  const Icon = ICON[stage];
  return (
    <span className={`fw-twl-chip ${GROUP_OF[stage]}`}>
      <Icon aria-hidden="true" />
      {label}
    </span>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const pct = (n: number, of: number) => (of > 0 ? Math.max(n > 0 ? 3 : 0, Math.round((n / of) * 100)) : 0);

type Load =
  | { kind: "loading" }
  | { kind: "ok"; data: TwlPipeline }
  | { kind: "unassigned" }
  | { kind: "failure"; status?: number; message: string };

function ResidentItem({ r, onRemind }: { r: TwlRow; onRemind: (patientId: string, next: TwlRemind) => void }) {
  const name = [r.first_name, r.last_name].filter(Boolean).join(" ") || "Name not recorded";
  return (
    <li className="fw-twl-resident">
      <div className="fw-twl-resident-who">
        <strong>{name}</strong>
        <span className="fw-muted">
          Room {r.room_number || "not recorded"} · {r.building || "Building name not recorded"}
          {r.building_closed ? " · closed building" : ""}
        </span>
      </div>
      <div className="fw-twl-resident-stage">
        <StageChip stage={r.stage} label={r.stage_label} />
        {r.earlier_case_closed && <span className="fw-muted">An earlier case was closed</span>}
        {r.test_age === "OVER_1Y" && (
          <span className="fw-twl-chip test"><Stethoscope aria-hidden="true" />{r.test_age_label}</span>
        )}
      </div>
      <dl className="fw-twl-resident-facts">
        <div>
          <dt>PA activity</dt>
          <dd>{r.pa_activity_label}</dd>
        </div>
        <div>
          <dt>TWL age</dt>
          <dd>{r.age_label}{r.before_2025 ? " · 2024 or earlier" : ""}</dd>
        </div>
        <div>
          <dt>Hearing test</dt>
          <dd>{r.test_age_label}</dd>
        </div>
      </dl>
      {r.remind && (
        <RemindPa facilityId={r.facility_id} patientId={r.patient_id} residentName={name} remind={r.remind}
          onSent={(next) => onRemind(r.patient_id, next)} />
      )}
    </li>
  );
}

export default function FieldTwlPipelineView() {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [filters, setFilters] = useState<TwlFilters>(NO_FILTERS);
  const [rows, setRows] = useState<TwlRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const request = useRef(0);
  // The building table scrolls inside its own box; say so only when it really overflows (real-browser QA, 2026-10-26).
  const tableWrap = useRef<HTMLDivElement | null>(null);
  const [tableScrolls, setTableScrolls] = useState(false);

  const read = useCallback(async (f: TwlFilters, offset: number) => {
    const mine = ++request.current;
    setBusy(true);
    if (offset === 0) setLoad((l) => (l.kind === "ok" ? l : { kind: "loading" }));
    try {
      const res = await twlPipelineApi.get(f, offset);
      if (mine !== request.current) return;
      if (res.success && res.data) {
        const data = res.data;
        setLoad({ kind: "ok", data });
        setRows((prev) => (offset === 0 ? data.rows : [...prev, ...data.rows]));
      } else if (res.status === 403 && /NO_CAPABILITY/.test(String(res.code || ""))) setLoad({ kind: "unassigned" });
      else setLoad({ kind: "failure", status: res.status, message: res.message || res.error || "The TWL pipeline could not be loaded." });
    } catch {
      if (mine === request.current) setLoad({ kind: "failure", message: "The server could not be reached." });
    } finally {
      if (mine === request.current) setBusy(false);
    }
  }, []);
  useEffect(() => { read(filters, 0); }, [filters, read]);
  useEffect(() => () => { request.current += 1; }, []);

  const data = load.kind === "ok" ? load.data : null;
  useEffect(() => {
    const measure = () => {
      const el = tableWrap.current;
      setTableScrolls(Boolean(el) && (el as HTMLDivElement).scrollWidth > (el as HTMLDivElement).clientWidth + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [data]);
  const filtered = Boolean(filters.stage || filters.age || filters.activity || filters.facility);
  const set = (patch: Partial<TwlFilters>) => { setExportMessage(null); setFilters((f) => ({ ...f, ...patch })); };
  // Remind PA: the row shows the reminder it just sent, without a reload.
  const onRemind = useCallback((patientId: string, next: TwlRemind) => {
    setRows((prev) => prev.map((x) => (x.patient_id === patientId ? { ...x, remind: next } : x)));
  }, []);
  const toggle = <K extends "stage" | "age" | "activity" | "facility">(key: K, value: TwlFilters[K]) =>
    set({ [key]: filters[key] === value ? "" : value } as Partial<TwlFilters>);
  const stageInfo = (k: TwlStage) => (data ? data.stages.find((s) => s.key === k) : undefined);
  const tooBig = data ? data.rows_total > EXPORT_MAX : false;

  const onExport = async () => {
    setExporting(true);
    setExportMessage(null);
    const error = await twlPipelineApi.exportCsv(filters);
    setExporting(false);
    setExportMessage(error || `The CSV was downloaded: ${plural(data ? data.rows_total : 0, "resident", "residents")}.`);
  };

  const buildingOptions = [{ value: "", label: "All buildings" }, ...(data ? data.buildings.map((b) => ({
    value: b.id, label: `${b.name || "Building name not recorded"}${b.closure ? ` · ${closureWords(b.closure)}` : ""}`,
  })) : [])];
  const notCounted = data
    ? data.not_counted.twl_not_qualified + data.not_counted.provider_tags_only + data.not_counted.provider_tags_disagree
    : 0;

  return (
    <Section
      title="TWL pipeline"
      subtitle="Every resident you qualified (TWL + Qualified in CounselEAR) across your buildings, and where each one is in the hearing-aid process."
      action={data ? (
        <Button variant="outline" type="button" className="fw-twl-action" onClick={onExport} disabled={exporting || tooBig || data.rows_total === 0}>
          <Download aria-hidden="true" />
          {exporting ? "Preparing CSV…" : "Export CSV"}
        </Button>
      ) : undefined}
    >
      <div className="fw-panel-content fw-twl">
        <div aria-live="polite" className="fw-twl-live">
          {busy && data && <p>Updating…</p>}
          {exportMessage && <p>{exportMessage}</p>}
          {tooBig && <p>{data?.rows_total} residents match, more than one file can carry ({EXPORT_MAX}). Narrow the filters to export.</p>}
        </div>
        {load.kind === "loading" && <TideState kind="loading" shape="tiles" label="Loading the TWL pipeline across your facilities…" />}
        {load.kind === "unassigned" && (
          <TideState kind="empty" reason="none" message="No building is assigned to you yet, so there is no TWL pipeline to show. Ask for a facility assignment." />
        )}
        {load.kind === "failure" && <TideState kind="error" status={load.status} message={load.message} onRetry={() => read(filters, 0)} />}

        {data && (
          <>
            {data.enterprise && <Notice>Your access is organisation-wide. This view counts only buildings assigned to you by name.</Notice>}
            {data.total === 0 && data.closed.residents === 0 ? (
              <TideState kind="empty" reason="none" message={`No TWL + Qualified residents across your ${plural(data.scope_buildings, "building", "buildings")}.`} />
            ) : (
              <>
                <div className="fw-twl-summary" role="group" aria-label="Summary">
                  <div className="fw-twl-stat">
                    <span>In the pipeline</span>
                    <strong>{data.total}</strong>
                    <small>across {plural(data.buildings.filter((b) => !b.closure).length, "open building", "open buildings")}</small>
                  </div>
                  <button type="button" className="fw-twl-stat alert" aria-pressed={filters.stage === "NO_OPEN_CASE"} onClick={() => toggle("stage", "NO_OPEN_CASE")}>
                    <span>No follow-up case yet</span>
                    <strong>{stageInfo("NO_OPEN_CASE")?.count ?? 0}</strong>
                    <small>no open Patient Advocate case</small>
                  </button>
                  <button type="button" className="fw-twl-stat" aria-pressed={filters.age === "GT_365"} onClick={() => toggle("age", "GT_365")}>
                    <span>Over 1 year old</span>
                    <strong>{data.ages.find((a) => a.key === "GT_365")?.count ?? 0}</strong>
                    <small>since the TWL date</small>
                  </button>
                </div>
                {notCounted > 0 && (
                  <p className="fw-twl-notcounted" role="note">
                    <strong>Not counted: {notCounted}.</strong>{" "}
                    {data.not_counted.twl_not_qualified > 0 && `${plural(data.not_counted.twl_not_qualified, "resident has", "residents have")} TWL in CounselEAR without Qualified (for example Medicaid pending). `}
                    {data.not_counted.provider_tags_only > 0 && `${plural(data.not_counted.provider_tags_only, "resident is", "residents are")} marked TWL Q in eMerge, and CounselEAR has no TWL or Qualified for them yet. `}
                    {data.not_counted.provider_tags_disagree > 0 && `${plural(data.not_counted.provider_tags_disagree, "resident is", "residents are")} marked TWL Q in eMerge, but CounselEAR records a different outcome (for example did not qualify). `}
                    They are not in the pipeline until CounselEAR says TWL and Qualified.
                  </p>
                )}

                <div className="fw-twl-funnel" role="group" aria-label="Stages">
                  {GROUPS.map((g) => (
                    <section key={g.key} className={`fw-twl-group ${g.key}`} aria-label={g.label}>
                      <h3>{g.label}</h3>
                      {g.stages.map((k) => {
                        const s = stageInfo(k);
                        if (!s) return null;
                        const Icon = ICON[k];
                        return (
                          <button key={k} type="button" className={`fw-twl-stage${filters.stage === k ? " active" : ""}`}
                            aria-pressed={filters.stage === k} onClick={() => toggle("stage", k)} title={s.what}>
                            <span className="fw-twl-stage-head">
                              <Icon aria-hidden="true" />
                              <span>{s.label}</span>
                              <strong>{s.count}</strong>
                            </span>
                            <span className="fw-twl-bar" aria-hidden="true"><span style={{ width: `${pct(s.count, data.total)}%` }} /></span>
                            <small>{s.what}{s.withheld ? ` ${s.withheld} not named here.` : ""}</small>
                          </button>
                        );
                      })}
                    </section>
                  ))}
                </div>

                <div className="fw-twl-ages" role="group" aria-label="How old each TWL is">
                  <h3>How old each TWL is</h3>
                  <div className="fw-twl-agebar" aria-hidden="true">
                    {data.ages.map((a) => <span key={a.key} className={`age-${a.key.toLowerCase()}`} style={{ flexGrow: a.count }} />)}
                  </div>
                  <div className="fw-twl-age-buttons">
                    {data.ages.map((a) => (
                      <button key={a.key} type="button" className={`fw-twl-age age-${a.key.toLowerCase()}${filters.age === a.key ? " active" : ""}`}
                        aria-pressed={filters.age === a.key} onClick={() => toggle("age", a.key)}>
                        <span className="fw-twl-swatch" aria-hidden="true" />
                        {a.label}
                        <strong>{a.count}</strong>
                      </button>
                    ))}
                  </div>
                  {(data.before_2025.count > 0 || data.test_over_1y.count > 0) && (
                    <div className="fw-twl-callout" role="note">
                      <p>
                        {data.before_2025.count > 0 && <><strong>{plural(data.before_2025.count, "TWL is", "TWLs are")} dated 2024 or earlier.</strong>{" "}</>}
                        {data.test_over_1y.count > 0 && <><strong>{plural(data.test_over_1y.count, "resident's", "residents'")} last hearing test is over 1 year old.</strong>{" "}</>}
                        They may need a new test before outreach.
                      </p>
                      {data.before_2025.count > 0 && (
                        <Button variant="outline" type="button" className="fw-twl-action" aria-pressed={filters.age === "BEFORE_2025"} onClick={() => toggle("age", "BEFORE_2025")}>
                          {filters.age === "BEFORE_2025" ? "Show every age" : "Show 2024 or earlier"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="fw-twl-activity" role="group" aria-label="PA activity">
                  <h3>PA activity</h3>
                  <div className="fw-twl-age-buttons">
                    {data.activities.map((a) => (
                      <button key={a.key} type="button" className={`fw-twl-age${filters.activity === a.key ? " active" : ""}`}
                        aria-pressed={filters.activity === a.key} onClick={() => toggle("activity", a.key)}>
                        {a.label}
                        <strong>{a.count}</strong>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="fw-twl-buildings">
                  <h3>By building</h3>
                  {tableScrolls && <p className="fw-twl-scroll-hint" aria-hidden="true">Scroll the table sideways for the counts.</p>}
                  <div className="fw-twl-table-wrap" ref={tableWrap} tabIndex={0} role="region" aria-label="TWL pipeline by building, scrollable">
                    <Table aria-label="TWL pipeline by building" className="[&_th]:whitespace-nowrap [&_td]:whitespace-nowrap [&_th]:text-foreground">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Building</TableHead>
                          <TableHead className="num">In pipeline</TableHead>
                          <TableHead className="num">No follow-up case</TableHead>
                          <TableHead className="num">Over 1 year</TableHead>
                          <TableHead className="num">Not named</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.buildings.map((b) => (
                          <TableRow key={b.id} className={b.closure ? "closed" : undefined}>
                            <TableCell>
                              <button type="button" className="fw-twl-link" aria-pressed={filters.facility === b.id} onClick={() => toggle("facility", b.id)}>
                                {b.name || "Building name not recorded"}
                              </button>
                              {b.closure && <span className="fw-muted"> · {closureWords(b.closure)}, not in the totals</span>}
                            </TableCell>
                            <TableCell className="num">{b.total}</TableCell>
                            <TableCell className="num">{b.never_followed_up}</TableCell>
                            <TableCell className="num">{b.over_1y}</TableCell>
                            <TableCell className="num">{b.withheld}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {data.closed.residents > 0 && (
                    <p className="fw-muted">
                      {plural(data.closed.residents, "resident", "residents")} at {plural(data.closed.buildings, "closed building is", "closed buildings are")} listed above but not counted in the totals.
                    </p>
                  )}
                </div>

                <div className="fw-twl-filters" role="group" aria-label="Filters">
                  <div className="fw-twl-filter">
                    <span>Stage</span>
                    <FieldSelect label="Stage" value={filters.stage} onChange={(v) => set({ stage: v as TwlFilters["stage"] })}
                      options={[{ value: "", label: "Every stage" }, ...data.stages.map((s) => ({ value: s.key, label: s.label }))]} />
                  </div>
                  <div className="fw-twl-filter">
                    <span>TWL age</span>
                    <FieldSelect label="TWL age" value={filters.age} onChange={(v) => set({ age: v as TwlFilters["age"] })}
                      options={[{ value: "", label: "Every age" }, ...[...TWL_AGES, "BEFORE_2025" as const].map((k) => ({ value: k, label: AGE_LABEL[k] }))]} />
                  </div>
                  <div className="fw-twl-filter">
                    <span>PA activity</span>
                    <FieldSelect label="PA activity" value={filters.activity} onChange={(v) => set({ activity: v as TwlActivity | "" })}
                      options={[{ value: "", label: "Any activity" }, ...data.activities.map((a) => ({ value: a.key, label: a.label }))]} />
                  </div>
                  <div className="fw-twl-filter">
                    <span>Building</span>
                    <FieldSelect label="Building" value={filters.facility} onChange={(v) => set({ facility: v })} options={buildingOptions} />
                  </div>
                  {data.closed.residents > 0 && (
                    <label className="fw-twl-check">
                      <input type="checkbox" checked={filters.includeClosed} onChange={(e) => set({ includeClosed: e.target.checked })} />
                      <span>Show residents at closed buildings</span>
                    </label>
                  )}
                  {filtered && <Button variant="ghost" type="button" className="fw-twl-action" onClick={() => set({ stage: "", age: "", activity: "", facility: "" })}>Clear filters</Button>}
                </div>

                <div className="fw-twl-list">
                  <h3>
                    Residents <span className="fw-muted">({rows.length < data.rows_total ? `${rows.length} of ${data.rows_total}` : data.rows_total} named)</span>
                  </h3>
                  {rows.length === 0 ? (
                    filtered || data.total - data.withheld > 0 ? (
                      <TideState kind="empty" reason="filtered" message="No named resident matches these filters."
                        onClear={() => set({ stage: "", age: "", activity: "", facility: "", includeClosed: true })} clearLabel="Clear filters" />
                    ) : (
                      <TideState kind="empty" reason="none" message="None of these residents can be named here: names show for buildings you cover and residents assigned to you." />
                    )
                  ) : (
                    <ul className="fw-twl-residents" aria-label="TWL residents">
                      {rows.map((r) => <ResidentItem key={r.patient_id} r={r} onRemind={onRemind} />)}
                    </ul>
                  )}
                  {data.truncated && rows.length < data.rows_total && (
                    <Button variant="outline" type="button" className="fw-twl-action fw-twl-more" disabled={busy} onClick={() => read(filters, rows.length)}>
                      Show more ({data.rows_total - rows.length} more)
                    </Button>
                  )}
                  {data.withheld > 0 && (
                    <p className="fw-twl-withheld">
                      {plural(data.withheld, "resident is", "residents are")} counted above but not named: names show for buildings you cover and residents assigned to you.
                    </p>
                  )}
                </div>
              </>
            )}
          </>
        )}
        <p className="fw-twl-footer">{FOOTER_LINE}</p>
      </div>
    </Section>
  );
}
